import {PhoneNumberUtil, PhoneNumberFormat} from "google-libphonenumber";
import moment from "moment";
import { TechnicianAddressType } from "../models/jobs";
import { uniqueNamesGenerator, NumberDictionary, animals } from "unique-names-generator";
import slug from "slug";
import { cities } from "./dictionaries";

export const isValidPhoneNumber = (number: string) => {  
  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
  const parsedPhoneNumber = phoneUtil.parse(number);
  return phoneUtil.isValidNumber(parsedPhoneNumber);
  } catch(err) {
    return false;
  }
}

export const uniqueName = () => {
  const numberDictionary = NumberDictionary.generate({ min: 10, max: 99 });
  const shortName = uniqueNamesGenerator({
    dictionaries: [cities, numberDictionary], // colors can be omitted here as not used
    separator: "",
    style: "capital",
    length: 2,
  });
  return slug(shortName);
};


export const isValidEmailAddress = (email: string) => {
  if(!email)
    return false;
  return /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(email);
}

export const e164PhoneNumber = (number:string) => {
  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
  const parsedPhoneNumber = phoneUtil.parse(number, "US");
  //This can still return partial/incomplete numbers, just formatted not validated
  return phoneUtil.format(parsedPhoneNumber, PhoneNumberFormat.E164);
  } catch (err) {
    return "";
  }
}

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export const snapshotToArray = (snapshot: { docs: any[]; }) => {
  var returnArr: unknown[] = [];

  snapshot.docs.forEach((childSnapshot)=> {
      var item = childSnapshot.data();
      item.id = childSnapshot.id;

      returnArr.push(item);
  });
  return returnArr;
};

export const formatAmount = (amount: number) => {
  if(parseFloat(amount.toFixed(0)) == amount)
    return amount.toFixed(0);
  else
    return amount.toFixed(2);
}

export const shorten = (str?: string, maxLen: number = 3) => {
  return str ? str.length > maxLen ? str.substring(0, maxLen) + "..." : str : "";
}

export const smartShorten = (str?: string) => {
  //return first 50 chars, and any non-space characters immediately following it until next space is reached
  return str ? str.length > 100 ? str.replace(/^(.{100}[^\s]*).*/, "$1") + "..." : str : "";
}

//https://firebasestorage.googleapis.com/v0/b/fleato-listing/o/211020%2F1634779595570-13829-pens4-jpg?alt=media&token=b870dcc5-87b8-459e-a70c-b5d8842cec09
export const friendlyFileNameFromImageUrl = (url: string): string => {
  const relativeUrl = url.split("/").pop();
  const noQueryParams = relativeUrl?.toString().split("?")[0];
  const noExcapedSlash = noQueryParams?.toString().split("%2F").pop();
  const noTimestampPrefix = noExcapedSlash?.toString().split("-").slice(1).join("-");
  const decoded = decodeURI(noTimestampPrefix?noTimestampPrefix:'');
  const extn = decoded.split("-").pop();
  const baseName = decoded.split("-").slice(0, -1).join("-");
  return `${baseName}.${extn}`;
}

export const clone = <T>(obj: T) => {
  return JSON.parse(JSON.stringify(obj)) as T;
};

export const longDateTime = (date: string) => moment(date).format("ddd MM/DD/YYYY hh:mm A");

export const zeroDecimals = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "" : (typeof amt == "string" ? parseFloat(amt).toFixed(0) : amt.toFixed(0))

export const zeroOrMoreDecimals = (amt:number | string | undefined) => parseFloat(zeroDecimalsFixed(amt)) > 100 ? zeroDecimalsFixed(amt) : twoDecimalsFixed(amt);

export const zeroDecimalsFixed = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "0" : (typeof amt == "string" ? parseFloat(amt).toFixed(0) : amt.toFixed(0))

export const twoDecimals = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "" : (typeof amt == "string" ? parseFloat(amt).toFixed(2) : amt.toFixed(2))

export const fourDecimals = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "" : (typeof amt == "string" ? parseFloat(amt).toFixed(4) : amt.toFixed(4))

export const sixDecimals = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "" : (typeof amt == "string" ? parseFloat(amt).toFixed(6) : amt.toFixed(6))

export const eightDecimals = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "" : (typeof amt == "string" ? parseFloat(amt).toFixed(8) : amt.toFixed(8))

export const twoDecimalsFixed = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "0.00" : (typeof amt == "string" ? parseFloat(amt).toFixed(2) : amt.toFixed(2))

export const threeDecimalsFixed = (amt:number | string | undefined) => (amt === undefined || amt === "NaN") ? "0.00" : (typeof amt == "string" ? parseFloat(amt).toFixed(3) : amt.toFixed(3))

export const onelineAddress = (address?: TechnicianAddressType) => address ? `${address.addressLine1}, ${address.addressLine2} ${address.addressLine2 ? ", " + address.addressLine2 : ""}, ${address.city}, ${address.state}, ${address.country} ${address.country}` : "";

export const onelineAddressWithoutComma = (address?: TechnicianAddressType) => address ? `${address.addressLine1} ${address.addressLine2} ${address.addressLine2 ? " " + address.addressLine2 : ""} ${address.city} ${address.state} ${address.country} ${address.zipCode}` : "";

export const recentEntry = (array: any[]): any => {
  if(!array?.length)
    return undefined;
  const last = array!.length -1;
  return array![last];
};

export const shortHex = (hex: string) => {
  return typeof hex === "string" && hex.length > 0 ? `${hex.slice(0,6)}...${hex.slice(-4)}` : "";
}

export const num = (n: string | undefined) => n === undefined ? 0 : (typeof n == "string" ? parseFloat(n) : n);

export const str = (s: { toString: () => any; } | undefined) => s === undefined ? "" : (typeof s == "string" ? s : s.toString());

export function titleCase(str: string) {
  if(!str)
    return "";
  return str.replace(/_/g, ' ').toLowerCase().split(' ').map(function(word) {
    return (word.charAt(0).toUpperCase() + word.slice(1));
  }).join(' ');
}

export const bankersRound = (n:number, d:number=2) => {
  var x = n * Math.pow(10, d);
  var r = Math.round(x);
  var br = (((((x>0)?x:(-x))%1)===0.5)?(((0===(r%2)))?r:(r-1)):r);
  return br / Math.pow(10, d);
}

export const randomShortUrl = () =>  {
  var result           = '';
  var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;
  for ( var i = 0; i < 8; i++ ) {
    result += characters.charAt(Math.floor(Math.random() * 
charactersLength));
 }
 return result;
}

export const numberKey = (orderId: number) => String(orderId).padStart(9, "0");

export const sequenceId = () => {
  const start = new Date("2022-07-24");
  const now = new Date();
  const elapsed = Math.floor((now.getTime() - start.getTime()) / 1000);
  return numberKey(elapsed);
};

export const initials = (name: string) => {
  let returnName = "";
  for(let i=0; i< name?.length; i++){
    if(i > 0 && name[i-1] === " " && name[i] != " "){
      returnName = returnName + name[i];
    }else if(i === 0 && name[i] != " "){
      returnName = returnName + name[i];
    }
  }
  const length = 2;

  var trimmedName = returnName.length > length ? returnName.substring(0, 2) : returnName;
  
  return trimmedName.toUpperCase();
}

export const getTime = (date: string, number: number) => {
  var myDate = new Date(date);
  myDate.setDate(myDate.getDate() + (number > 0 ? number : 0));
  if(number != 0) {
    return `${myDate.getUTCMonth()}-${myDate.getDate()}-${myDate.getFullYear()}`;
  }else {
    return `${myDate.getUTCMonth()}-${myDate.getDate()}-${myDate.getFullYear()}, ${myDate.getHours()} : ${myDate.getMinutes() < 10 ? "0" : ""}${myDate.getMinutes()} ${myDate.getHours() < 12 ? "AM" : "PM"}`;
  }
}

export const daysArr = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
